import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { useNotifications } from "../../context/NotificationsContext.js";
import { toView, type ViewNotification } from "./notification-model.js";
import { CategoryTile, FOCUS } from "./notification-ui.js";

/** Matches the .notif-toast-bar keyframe duration. */
const TOAST_MS = 4500;
const OUT_MS = 260;

/**
 * One toast at a time, bottom-right, for a newly arrived notification.
 *
 * It listens to the store's `lastArrival`, which is set only by the existing SSE
 * stream — no new transport. A newer arrival replaces the current toast and
 * restarts the timer.
 */
export function NotificationToast() {
  const { lastArrival } = useNotifications();
  const [item, setItem] = useState<ViewNotification | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!lastArrival) return;
    setItem(toView(lastArrival));
    setLeaving(false);
  }, [lastArrival]);

  // Self-dismiss. The timer is keyed on the item's id, so a newer arrival
  // restarts it rather than inheriting the old one's remaining time.
  useEffect(() => {
    if (!item) return;
    const close = setTimeout(() => setLeaving(true), TOAST_MS);
    return () => clearTimeout(close);
  }, [item]);

  useEffect(() => {
    if (!leaving) return;
    const done = setTimeout(() => { setItem(null); setLeaving(false); }, OUT_MS);
    return () => clearTimeout(done);
  }, [leaving]);

  if (!item) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed bottom-7 right-7 z-[300] w-[360px] overflow-hidden rounded-xl border border-[var(--color-border)] border-l-[3px] border-l-brand-500 bg-surface-0 shadow-[0_16px_40px_rgb(16_24_40_/_0.18)]",
        leaving ? "notif-toast-out" : "notif-toast-in",
      )}
    >
      <div className="flex gap-3 p-3.5">
        <CategoryTile category={item.category} />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.5px] text-brand-600">New notification</p>
          <p className="mt-0.5 truncate text-[13.5px] font-semibold text-surface-900">{item.title}</p>
          <p className="mt-0.5 line-clamp-2 text-[12.5px] text-surface-500">{item.body}</p>
        </div>
        <button
          type="button"
          onClick={() => setLeaving(true)}
          aria-label="Dismiss notification"
          className={cn("h-6 w-6 shrink-0 rounded text-surface-400 hover:text-surface-700", FOCUS)}
        >
          <X size={15} />
        </button>
      </div>
      {/* Shrinks left-to-right over the toast's lifetime. */}
      <span aria-hidden="true" className="notif-toast-bar block h-[3px] w-full bg-brand-500" />
    </div>
  );
}
